import React, { useEffect, useState, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import Layout from './components/Layout/Layout';
import GoogleAuthComplete from './pages/Login/GoogleAuthComplete';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import Repositories from './pages/Repositories/Repositories';
import Members from './pages/Members/Members';
import MemberDetail from './pages/MemberDetail/MemberDetail';
import RepositoryDetail from './pages/RepositoryDetail/RepositoryDetail';
import Profile from './pages/Profile/Profile';
import Notifications from './pages/Notifications/Notifications';
import SetupOrganisation from './pages/SetupOrganisation/SetupOrganisation';
import Streams from './pages/Streams/Streams';
import Projects from './pages/Projects/Projects';
import ProjectDetail from './pages/ProjectDetail/ProjectDetail';
import InviteAccept from './pages/InviteAccept/InviteAccept';
import NotFound from './pages/NotFound/NotFound';
import VantiWindow from './pages/VantiWindow/VantiWindow';
import {
  isAuthenticated,
  setUnauthorizedHandler,
  setGitHubSetupRequiredHandler,
  getSetupStatus,
} from './services/api';
import { FEATURE_FLAG_DEFAULTS } from './lib/featureFlagDefaults';
import { useDeployVersionWatcher } from './hooks/useDeployVersionWatcher';
import './App.css';

const Landing = lazy(() => import('./pages/Landing'));
const Docs = lazy(() => import('./pages/Docs/Docs'));
const ConfigurationLayout = lazy(() => import('./pages/Configuration/ConfigurationLayout'));
const ConfigurationSystemTab = lazy(() => import('./pages/Configuration/tabs/SystemTab'));
const ConfigurationIndexingTab = lazy(() => import('./pages/Configuration/tabs/IndexingTab'));
const ConfigurationVantiTab = lazy(() => import('./pages/Configuration/tabs/VantiTab'));
const ConfigurationEmailTab = lazy(() => import('./pages/Configuration/tabs/EmailTab'));
const ConfigurationSyncMonitoringTab = lazy(
  () => import('./pages/Configuration/tabs/SyncMonitoringTab')
);
const ConfigurationCursorUsageTab = lazy(
  () => import('./pages/Configuration/tabs/CursorUsageTab')
);
const ConfigurationSyncMonitoringTokenLogsTab = lazy(
  () => import('./pages/Configuration/tabs/SyncMonitoringTokenLogsTab')
);
const OrganisationSettingsLayout = lazy(
  () => import('./pages/OrganisationSettings/OrganisationSettingsLayout'),
);
const OrganisationGeneralTab = lazy(
  () => import('./pages/OrganisationSettings/tabs/OrganisationGeneralTab'),
);
const OrganisationGitHubTab = lazy(
  () => import('./pages/OrganisationSettings/tabs/OrganisationGitHubTab'),
);
const OrganisationAiTab = lazy(() => import('./pages/OrganisationSettings/tabs/OrganisationAiTab'));
const UserSettingsLayout = lazy(() => import('./pages/UserSettings/UserSettingsLayout'));
const UserSettingsIndex = lazy(() =>
  import('./pages/UserSettings/UserSettingsLayout').then((m) => ({ default: m.UserSettingsIndex }))
);
const UserIntegrationsTab = lazy(() => import('./pages/UserSettings/tabs/UserIntegrationsTab'));
const UserAssetsTab = lazy(() => import('./pages/UserSettings/tabs/UserAssetsTab'));
const UserMyAssetDetail = lazy(() => import('./pages/UserSettings/tabs/UserMyAssetDetail'));
const UserSecurityTab = lazy(() => import('./pages/UserSettings/tabs/UserSecurityTab'));
const UserProfileTab = lazy(() => import('./pages/UserSettings/tabs/UserProfileTab'));
const HR = lazy(() => import('./pages/HR/HR'));
const AttendanceRegisters = lazy(() => import('./pages/AttendanceRegister/AttendanceRegisters'));
const AttendanceRegisterCreate = lazy(
  () => import('./pages/AttendanceRegister/AttendanceRegisterCreate'),
);
const AttendanceRegisterDetail = lazy(
  () => import('./pages/AttendanceRegister/AttendanceRegisterDetail'),
);
const AssetTrackingAdminGuard = lazy(
  () => import('./pages/AssetTracking/AssetTrackingAdminGuard'),
);
const AssetTracking = lazy(() => import('./pages/AssetTracking/AssetTracking'));
const AssetsTab = lazy(() => import('./pages/AssetTracking/AssetsTab'));
const AssetTrackingMembersTab = lazy(
  () => import('./pages/AssetTracking/AssetTrackingMembersTab'),
);
const AssetTrackingReportsTab = lazy(
  () => import('./pages/AssetTracking/AssetTrackingReportsTab'),
);
const AssetEdit = lazy(() => import('./pages/AssetTracking/AssetEdit'));
const AssetMemberDetail = lazy(() => import('./pages/AssetTracking/AssetMemberDetail'));
const LeaveApplicationsAdminGuard = lazy(
  () => import('./pages/LeaveApplications/LeaveApplicationsAdminGuard'),
);
const LeaveApplications = lazy(() => import('./pages/LeaveApplications/LeaveApplications'));
const LeaveApplicationsAdmin = lazy(
  () => import('./pages/LeaveApplications/LeaveApplicationsAdmin'),
);
const LeaveApplicationsAdminMemberDetail = lazy(
  () => import('./pages/LeaveApplications/LeaveApplicationsAdminMemberDetail'),
);
const AttendanceRegisterPublic = lazy(
  () => import('./pages/AttendanceRegister/AttendanceRegisterPublic'),
);
const Matrix = lazy(() => import('./pages/Matrix/Matrix'));
const Portfolios = lazy(() => import('./pages/Portfolios/Portfolios'));
const PortfolioDetail = lazy(() => import('./pages/Portfolios/PortfolioDetail'));
const PerformanceReviewsHub = lazy(() => import('./pages/PerformanceReviews/PerformanceReviewsHub'));
const PerformanceReviewCyclesTab = lazy(
  () => import('./pages/PerformanceReviews/PerformanceReviewCyclesTab'),
);
const PerformanceReviewQuestionBankPage = lazy(
  () => import('./pages/PerformanceReviews/PerformanceReviewQuestionBankPage'),
);
const PerformanceReviewQuestionTemplateEdit = lazy(
  () => import('./pages/PerformanceReviews/PerformanceReviewQuestionTemplateEdit'),
);
const PerformanceReviewCycleEdit = lazy(
  () => import('./pages/PerformanceReviews/PerformanceReviewCycleEdit'),
);
const PerformanceReviewCycleDetail = lazy(
  () => import('./pages/PerformanceReviews/PerformanceReviewCycleDetail'),
);
const PerformanceReviewSubjectDetail = lazy(
  () => import('./pages/PerformanceReviews/PerformanceReviewCycleDetail/SubjectDetail'),
);
const PerformanceReviewValuesPage = lazy(
  () => import('./pages/PerformanceReviews/PerformanceReviewValuesPage'),
);
const ReviewRespond = lazy(() => import('./pages/PerformanceReviews/ReviewRespond'));
const ReviewPortal = lazy(() => import('./pages/PerformanceReviews/ReviewPortal'));
const SuggestionBoxPublic = lazy(() => import('./pages/SuggestionBox/SuggestionBoxPublic'));
const SuggestionBoxAdmin = lazy(() => import('./pages/SuggestionBox/SuggestionBoxAdmin'));
const Surveys = lazy(() => import('./pages/Surveys/hub/SurveysHub'));
const SurveyEdit = lazy(() => import('./pages/Surveys/SurveyEdit'));
const SurveyQuestionEdit = lazy(() => import('./pages/Surveys/SurveyQuestionEdit'));
const SesQuestionEdit = lazy(() => import('./pages/Ses/question/SesQuestionEdit'));
const SurveyEmailListEdit = lazy(() => import('./pages/Surveys/SurveyEmailListEdit'));
const SurveySessionNew = lazy(() => import('./pages/Surveys/SurveySessionNew'));
const SurveySessionDetail = lazy(() => import('./pages/Surveys/SurveySessionDetail'));
const SurveySessionResults = lazy(() => import('./pages/Surveys/SurveySessionResults'));
const SurveyRespond = lazy(() => import('./pages/Surveys/SurveyRespond'));
const Roles = lazy(() => import('./pages/Roles/Roles'));
const RolesLayout = lazy(() => import('./pages/Roles/RolesLayout'));
const RoleForm = lazy(() => import('./pages/Roles/RoleForm'));

function PageFallback() {
  return <div className="px-6 py-12 text-sm text-slate-400">Loading…</div>;
}

const SesHub = lazy(() => import('./pages/Ses/hub/SesHub'));
const SesSessionDetail = lazy(() => import('./pages/Ses/session/SesSessionDetail'));
const SesSessionBuilder = lazy(() => import('./pages/Ses/edit/SesSessionBuilder'));
const SesCycleDetail = lazy(() => import('./pages/Ses/cycle/SesCycleDetail'));
const SesPortal = lazy(() => import('./pages/Ses/portal/SesPortal'));
const SesRespond = lazy(() => import('./pages/Ses/portal/SesRespond'));
const SesAssigneePreview = lazy(() => import('./pages/Ses/SesAssigneePreview'));

function SesPageFallback() {
  return <PageFallback />;
}

const SIGN_IN_PATH = FEATURE_FLAG_DEFAULTS['ui.auth.passwordLogin'] ? '/login' : '/';

const ProtectedRoute = ({ children }: any) => {
  const location = useLocation();
  return isAuthenticated() ? (
    children
  ) : (
    <Navigate to={SIGN_IN_PATH} replace state={{ from: location }} />
  );
};

/** At `/`, show marketing landing when logged out; otherwise render the app shell. */
function LandingOrApp({ children }: any) {
  const location = useLocation();
  if (!isAuthenticated()) {
    if (location.pathname === '/') {
      return (
        <Suspense fallback={<PageFallback />}>
          <Landing />
        </Suspense>
      );
    }
    return <Navigate to={SIGN_IN_PATH} replace state={{ from: location }} />;
  }
  return children;
}

/** When authenticated, redirect to /setup/organisation if org setup is required. */
function SetupGuard({ children }: any) {
  const [requiresSetup, setRequiresSetup] = useState<any>(null);

  useEffect(() => {
    getSetupStatus()
      .then((data) =>
        setRequiresSetup(
          data.requiresOrganisationSetup === true || data.requiresGitHubSetup === true
        )
      )
      .catch(() => setRequiresSetup(false));
  }, []);

  if (requiresSetup === null) {
    return null;
  }
  if (requiresSetup) {
    return <Navigate to="/setup/organisation" replace />;
  }
  return children;
}

/** Legacy `/surveys/:id` (no `/edit`) — send users to Survey Creator. */
function RedirectSurveyIdToEdit() {
  const { surveyId } = useParams();
  return <Navigate to={`/surveys/${surveyId}/edit`} replace />;
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [setupStatus, setSetupStatus] = useState({ hasUsers: true });

  useDeployVersionWatcher();

  useEffect(() => {
    setUnauthorizedHandler(() => navigate(SIGN_IN_PATH, { replace: true }));
    setGitHubSetupRequiredHandler(() => navigate('/setup/organisation', { replace: true }));
  }, [navigate]);

  useEffect(() => {
    getSetupStatus()
      .then((data) => setSetupStatus({ hasUsers: data.hasUsers }))
      .catch(() => setSetupStatus({ hasUsers: true }));
  }, []);

  const noUsersYet = setupStatus.hasUsers === false;
  const showOnlyRegister = noUsersYet && location.pathname === '/login';

  return (
    <Routes>
      {showOnlyRegister ? (
        <Route path="/login" element={<Navigate to="/register" replace />} />
      ) : (
        <Route path="/login" element={<Login />} />
      )}
      <Route path="/auth/google/complete" element={<GoogleAuthComplete />} />
      <Route path="/register" element={<Register hasUsers={setupStatus.hasUsers} />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/accept" element={<InviteAccept />} />
      <Route path="/attendance/register" element={<Suspense fallback={<PageFallback />}><AttendanceRegisterPublic /></Suspense>} />
      <Route path="/survey/respond" element={<Suspense fallback={<PageFallback />}><SurveyRespond /></Suspense>} />
      <Route path="/review/respond" element={<Suspense fallback={<PageFallback />}><ReviewRespond /></Suspense>} />
      <Route
        path="/ses/me"
        element={
          <ProtectedRoute>
            <Suspense fallback={<SesPageFallback />}>
              <SesPortal />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ses/respond/:taskId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<SesPageFallback />}>
              <SesRespond />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ses/:sesId/preview/:assigneeId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<SesPageFallback />}>
              <SesAssigneePreview />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route path="/review/cycles/:cycleId/my-surveys" element={<Suspense fallback={<PageFallback />}><ReviewPortal /></Suspense>} />
      <Route path="/suggest" element={<Suspense fallback={<PageFallback />}><SuggestionBoxPublic /></Suspense>} />
      <Route
        path="/docs"
        element={
          <ProtectedRoute>
            <SetupGuard>
              <Suspense fallback={<PageFallback />}>
                <Docs />
              </Suspense>
            </SetupGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/organisation"
        element={
          <ProtectedRoute>
            <SetupOrganisation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vanti"
        element={
          <ProtectedRoute>
            <SetupGuard>
              <VantiWindow />
            </SetupGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <LandingOrApp>
            <ProtectedRoute>
              <SetupGuard>
                <Layout />
              </SetupGuard>
            </ProtectedRoute>
          </LandingOrApp>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="streams/:streamId/projects/:projectId" element={<ProjectDetail />} />
        <Route path="streams/:streamId" element={<Streams />} />
        <Route path="streams" element={<Streams />} />
        <Route path="projects" element={<Projects />} />
        <Route path="repositories" element={<Repositories />} />
        <Route path="repositories/:id" element={<RepositoryDetail />} />
        <Route path="members" element={<Members />} />
        <Route path="members/:memberId" element={<MemberDetail />} />
        <Route path="commits" element={<Navigate to="/repositories" replace />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings/account" element={<UserSettingsLayout />}>
          <Route index element={<UserSettingsIndex />} />
          <Route path="profile" element={<UserProfileTab />} />
          <Route path="integrations" element={<UserIntegrationsTab />} />
          <Route path="assets" element={<UserAssetsTab />} />
          <Route path="assets/:assetId" element={<UserMyAssetDetail />} />
          <Route path="security" element={<UserSecurityTab />} />
        </Route>
        <Route path="settings/organisation" element={<OrganisationSettingsLayout />}>
          <Route index element={<Navigate to="general" replace />} />
          <Route path="general" element={<OrganisationGeneralTab />} />
          <Route path="github" element={<OrganisationGitHubTab />} />
          <Route path="ai" element={<OrganisationAiTab />} />
          <Route path="email" element={<ConfigurationEmailTab />} />
        </Route>
        <Route path="contributors" element={<Navigate to="/" replace />} />
        <Route path="statistics" element={<Navigate to="/" replace />} />
        <Route path="pull-requests" element={<Navigate to="/repositories" replace />} />
        <Route path="branches" element={<Navigate to="/repositories" replace />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="configuration" element={<ConfigurationLayout />}>
          <Route index element={<Navigate to="system" replace />} />
          <Route path="system" element={<ConfigurationSystemTab />} />
          <Route path="email" element={<Navigate to="/settings/organisation/email" replace />} />
          <Route path="indexing" element={<ConfigurationIndexingTab />} />
          <Route path="sync-monitoring" element={<ConfigurationSyncMonitoringTab />} />
          <Route
            path="sync-monitoring/tokens/:tokenId"
            element={<ConfigurationSyncMonitoringTokenLogsTab />}
          />
          <Route path="vanti/*" element={<ConfigurationVantiTab />} />
          <Route path="cursor-usage" element={<ConfigurationCursorUsageTab />} />
          <Route path="roles" element={<RolesLayout />}>
            <Route index element={<Roles />} />
            <Route path="new" element={<RoleForm />} />
            <Route path=":roleId/edit" element={<RoleForm />} />
          </Route>
        </Route>
        <Route path="roles" element={<Navigate to="/configuration/roles" replace />} />
        <Route path="hr" element={<HR />} />
        <Route
          path="ses"
          element={
            <Suspense fallback={<SesPageFallback />}>
              <SesHub />
            </Suspense>
          }
        />
        <Route path="/ses/:sesId/questions/new" element={<SesQuestionEdit />} />
        <Route path="/ses/:sesId/questions/:questionId/edit" element={<SesQuestionEdit />} />
        <Route
          path="ses/:sesId/edit"
          element={
            <Suspense fallback={<SesPageFallback />}>
              <SesSessionBuilder />
            </Suspense>
          }
        />
        <Route
          path="ses/:sesId/cycles/:cycleId"
          element={
            <Suspense fallback={<SesPageFallback />}>
              <SesCycleDetail />
            </Suspense>
          }
        />
        <Route
          path="ses/:sesId"
          element={
            <Suspense fallback={<SesPageFallback />}>
              <SesSessionDetail />
            </Suspense>
          }
        />
        <Route path="suggestion-box" element={<SuggestionBoxAdmin />} />
        <Route path="attendance-registers" element={<AttendanceRegisters />} />
        <Route path="attendance-registers/new" element={<AttendanceRegisterCreate />} />
        <Route path="attendance-registers/:registerId" element={<AttendanceRegisterDetail />} />
        <Route element={<AssetTrackingAdminGuard />}>
          <Route path="asset-tracking" element={<AssetTracking />}>
            <Route index element={<AssetsTab />} />
            <Route path="members" element={<AssetTrackingMembersTab />} />
            <Route path="reports" element={<AssetTrackingReportsTab />} />
          </Route>
          <Route path="asset-tracking/assets/:assetId/edit" element={<AssetEdit />} />
          <Route path="asset-tracking/members/:userId" element={<AssetMemberDetail />} />
        </Route>
        <Route element={<LeaveApplicationsAdminGuard />}>
          <Route
            path="leave-applications/admin/members/:userId"
            element={<LeaveApplicationsAdminMemberDetail />}
          />
          <Route path="leave-applications/admin/*" element={<LeaveApplicationsAdmin />} />
        </Route>
        <Route path="leave-applications/*" element={<LeaveApplications />} />
        <Route path="matrix" element={<Matrix />} />
        <Route path="surveys" element={<Surveys />} />
        <Route path="surveys/sessions/new" element={<SurveySessionNew />} />
        <Route path="surveys/sessions/:sessionId/setup" element={<SurveySessionNew />} />
        <Route path="surveys/sessions/:sessionId" element={<SurveySessionDetail />} />
        <Route
          path="surveys/sessions/:sessionId/results/:surveyId"
          element={<SurveySessionResults />}
        />
        <Route path="surveys/questions/new" element={<SurveyQuestionEdit />} />
        <Route path="surveys/questions/:questionId/edit" element={<SurveyQuestionEdit />} />
        <Route path="surveys/email-lists/:listId/edit" element={<SurveyEmailListEdit />} />
        <Route
          path="surveys/new"
          element={<Navigate to="/surveys?tab=surveys&newSurvey=1" replace />}
        />
        <Route path="surveys/:surveyId/edit" element={<SurveyEdit />} />
        <Route path="surveys/:surveyId" element={<RedirectSurveyIdToEdit />} />
        <Route path="library" element={<Navigate to="/portfolios" replace />} />
        <Route path="portfolios" element={<Portfolios />} />
        <Route path="portfolios/:streamId/:projectId" element={<PortfolioDetail />} />
        <Route
          path="performance-reviews/questions/:raterRole/templates/new"
          element={<PerformanceReviewQuestionTemplateEdit />}
        />
        <Route
          path="performance-reviews/questions/:raterRole/templates/:templateId/edit"
          element={<PerformanceReviewQuestionTemplateEdit />}
        />
        <Route path="performance-reviews" element={<PerformanceReviewsHub />}>
          <Route index element={<PerformanceReviewCyclesTab />} />
          <Route path="questions/:raterRole" element={<PerformanceReviewQuestionBankPage />} />
          <Route path="values" element={<PerformanceReviewValuesPage />} />
        </Route>
        <Route path="performance-reviews/:cycleId/edit" element={<PerformanceReviewCycleEdit />} />
        <Route
          path="performance-reviews/:cycleId/detail"
          element={<PerformanceReviewCycleDetail />}
        />
        <Route
          path="performance-reviews/:cycleId/subjects/:subjectEmail"
          element={<PerformanceReviewSubjectDetail />}
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <Router future={{ v7_relativeSplatPath: true }}>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </Router>
  );
}

export default App;
